import React, { FC } from "react";
import { useParams } from "react-router-dom";

import { useApisAdminOperatorsCompaniesPostsReportsIndex } from "~/hooks";

import { AdminOperatorLayout } from "~/components/layouts";

export const AdminOperatorsCompaniesPostsReportsIndex: FC = () => {
  const { companyId = "" } = useParams<{ companyId: string }>();

  const { data } = useApisAdminOperatorsCompaniesPostsReportsIndex({
    companyId,
  });

  const company = data?.company;
  const postsReports = data?.postsReports;

  const breadCrumbItems = [
    {
      label: "運営者画面",
      href: "/admin_operators",
    },
    {
      label: "会社一覧",
      href: "/admin_operators",
    },
    {
      label: company?.name || "",
      href: `/admin_operators/companies/${companyId}`,
    },
    {
      label: "投稿レポート",
      href: `/admin_operators/companies/${companyId}/posts_reports`,
    },
  ];

  const reportItems = [
    { label: "部署", data: postsReports?.businessDivisions },
    { label: "課", data: postsReports?.businessSections },
    { label: "社員", data: postsReports?.employees },
    { label: "職種", data: postsReports?.jobCategories },
    { label: "サービス", data: postsReports?.providingServices },
    { label: "インサイト投稿", data: postsReports?.insightPosts },
    { label: "インサイト投稿いいね", data: postsReports?.insightPostLikes },
    {
      label: "インサイト投稿コメント",
      data: postsReports?.insightPostComments,
    },
    {
      label: "インサイト投稿ブックマーク",
      data: postsReports?.insightPostBookmarks,
    },
    {
      label: "インサイト投稿共有",
      data: postsReports?.insightPostSharedEmployees,
    },
    {
      label: "インサイト投稿コメントいいね",
      data: postsReports?.insightPostCommentLikes,
    },
    {
      label: "ナレッジインサイト投稿",
      data: postsReports?.knowledgeInsightPosts,
    },
    {
      label: "ナレッジインサイト投稿コメント",
      data: postsReports?.knowledgeInsightPostComments,
    },
    { label: "改善アイデア", data: postsReports?.kaizenIdeas },
    { label: "改善アイデアいいね", data: postsReports?.kaizenIdeaLikes },
    { label: "改善アイデア返信", data: postsReports?.kaizenIdeaReplies },
    {
      label: "改善アイデア返信いいね",
      data: postsReports?.kaizenIdeaReplyLikes,
    },
    {
      label: "フォーカスチャレンジ",
      data: postsReports?.focusChallengeEmployees,
    },
    {
      label: "フォーカスチャレンジいいね",
      data: postsReports?.focusChallengeEmployeeLikes,
    },
    {
      label: "フォーカスチャレンジ返信",
      data: postsReports?.focusChallengeEmployeeReplies,
    },
    { label: "ミーティング", data: postsReports?.meetingNotes },
    {
      label: "ミーティングテンプレート",
      data: postsReports?.meetingNoteTemplates,
    },
    { label: "ダイレクトメッセージ", data: postsReports?.directMessages },
    {
      label: "ダイレクトメッセージいいね",
      data: postsReports?.directMessageLikes,
    },
    { label: "チェックアウト", data: postsReports?.employeeSurveys },
    {
      label: "チェックアウト回答",
      data: postsReports?.employeeSurveySubmissions,
    },
    {
      label: "チェックアウト回答いいね",
      data: postsReports?.employeeSurveySubmissionLikes,
    },
    {
      label: "チェックアウト回答ブックマーク",
      data: postsReports?.employeeSurveySubmissionBookmarks,
    },
    {
      label: "チェックアウト回答返信",
      data: postsReports?.employeeSurveySubmissionReplies,
    },
    { label: "スキル評価期間", data: postsReports?.skillEvaluationTerms },
    { label: "KPI期間", data: postsReports?.kpiTerms },
    { label: "資料カテゴリー", data: postsReports?.documentFileCategories },
    { label: "Q&Aカテゴリー", data: postsReports?.questionAnswerCategories },
    { label: "ロールプレイング", data: postsReports?.roleplayingConditions },
    { label: "ブログカテゴリー", data: postsReports?.salesConceptCategories },
  ];

  return (
    <AdminOperatorLayout breadCrumbItems={breadCrumbItems}>
      <h1 className="text-xl mb-4">
        <span className="font-bold">{company?.name}</span>の投稿レポート
      </h1>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 border border-gray-200 bg-white sticky top-0 left-0 z-layout text-left font-semibold text-gray-700 min-w-[200px]">
                項目
              </th>
              {data?.dates.map((date, index) => (
                <th
                  key={index}
                  className="px-4 py-3 border border-gray-200 bg-white sticky top-0 z-layout text-center font-semibold text-gray-700"
                >
                  {date}
                </th>
              ))}
              <th className="px-4 py-3 border border-gray-200 bg-white sticky top-0 z-layout text-center font-semibold text-gray-700">
                合計
              </th>
            </tr>
          </thead>
          <tbody>
            {reportItems.map((item, index) => {
              const total =
                item.data?.reduce((sum, report) => sum + report.count, 0) || 0;
              return (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 border border-gray-200 bg-white sticky left-0 z-layout font-bold text-gray-700 min-w-[200px]">
                    {item.label}
                  </td>
                  {data?.dates.map((date, dateIndex) => {
                    const report = item.data?.find((r) => r.date === date);
                    return (
                      <td
                        key={dateIndex}
                        className="px-4 py-3 border border-gray-200 text-right text-gray-600"
                      >
                        {report?.count || 0}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 border border-gray-200 text-right font-semibold text-gray-700 bg-gray-50">
                    {total}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AdminOperatorLayout>
  );
};
